import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../config";
import s from "./TeacherDetailModal.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function photoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${API_BASE}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
}
function splitSchedule(raw) {
  if (!raw) return { days: "", time: "" };
  const idx = raw.lastIndexOf(" ");
  if (idx === -1) return { days: raw, time: "" };
  return { days: raw.slice(0, idx), time: raw.slice(idx + 1) };
}

/**
 * Lighter sibling of StudentDetailModal — focused on a teacher (Employee
 * row). Shows contact + classes taught in the active term. Read-only.
 *
 * Props:
 *   open         boolean
 *   teacher      Employee row (must have id, name; ideally photoUrl, contact, email, position)
 *   offerings    optional Array<ClassOffering>  — used to show classes in active term
 *   subjects     optional Array<Subject>
 *   sections     optional Array<Section>
 *   gradeLevels  optional Array<GradeLevel>
 *   activeSyId   optional number — when set, only offerings whose semester is in this SY
 *   activeSemId  optional number — when set, only offerings in this exact semester
 *   semesters    optional Array<Semester> — needed if activeSyId is used (to map sem→sy)
 *   onClose      fn()
 */
export default function TeacherDetailModal({
  open,
  teacher,
  offerings   = [],
  subjects    = [],
  sections    = [],
  gradeLevels = [],
  semesters   = [],
  activeSyId  = null,
  activeSemId = null,
  onClose,
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Resolve current-term offerings for this teacher.
  const myOfferings = useMemo(() => {
    if (!teacher?.id) return [];
    const semesterToSy = new Map(semesters.map(sm => [sm.id, sm.schoolYearId]));
    return (offerings || []).filter(o => {
      if (String(o.teacherStaffId) !== String(teacher.id)) return false;
      if (activeSemId && String(o.semesterId) !== String(activeSemId)) return false;
      if (activeSyId && !activeSemId) {
        // Filter by SY via semester→SY map.
        if (String(semesterToSy.get(o.semesterId)) !== String(activeSyId)) return false;
      }
      return true;
    });
  }, [teacher?.id, offerings, semesters, activeSyId, activeSemId]);

  const subjectsById = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);
  const sectionsById = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const gradeById    = useMemo(() => Object.fromEntries(gradeLevels.map(x => [x.id, x])), [gradeLevels]);

  if (!open || !teacher) return null;

  const photo = photoSrc(teacher.photoUrl);
  const name  = teacher.name || "(Unnamed)";
  const subtitle = [teacher.position, teacher.department || teacher.departmentName]
    .filter(Boolean).join(" · ");

  return createPortal(
    <div
      className={s.backdrop}>
      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tdm-title">
        <div className={s.head}>
          <div
            className={s.avatar}
            style={photo ? { backgroundImage: `url(${photo})` }
                         : { backgroundColor: avatarColor(name) }}
            aria-hidden="true">
            {!photo && initials(name)}
          </div>
          <div className={s.headMain}>
            <div className={s.eyebrow}>Teacher</div>
            <h3 id="tdm-title" className={s.title}>{name}</h3>
            {subtitle && <div className={s.subtitle}>{subtitle}</div>}
          </div>
          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>

        <div className={s.body}>
          {(teacher.email || teacher.contact) && (
            <div className={s.section}>
              <div className={s.sectionHead}>
                <span className={s.sectionTitle}>Contact</span>
              </div>
              <div className={s.contactList}>
                {teacher.email && (
                  <span>✉ <a href={`mailto:${teacher.email}`}>{teacher.email}</a></span>
                )}
                {teacher.contact && <span>📞 {teacher.contact}</span>}
              </div>
            </div>
          )}

          <div className={s.section}>
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Classes</span>
              <span className={s.sectionSub}>
                {myOfferings.length} class{myOfferings.length === 1 ? "" : "es"}
              </span>
            </div>
            {myOfferings.length === 0 ? (
              <div className={s.empty}>
                No classes assigned in this term.
              </div>
            ) : (
              <ul className={s.classList}>
                {myOfferings.map(o => {
                  const subj  = subjectsById[o.subjectId];
                  const sec   = sectionsById[o.sectionId];
                  const grade = sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                  const { days } = splitSchedule(o.schedule);
                  return (
                    <li key={o.id} className={s.classRow}>
                      <div className={s.classCode}>{subj?.code || "—"}</div>
                      <div className={s.classMain}>
                        <div className={s.classTitle}>{subj?.title || "—"}</div>
                        <div className={s.classMeta}>
                          {sec
                            ? (grade ? `${grade.name} · ${sec.name}` : sec.name)
                            : "—"}
                        </div>
                      </div>
                      <div className={s.classDays}>{days || "—"}</div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className={s.foot}>
          <button type="button" className={s.btnGhost} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
